import React, { useState, useEffect } from 'react';
import { 
  Ticket, 
  Plus, 
  Trash2, 
  Edit, 
  Calendar, 
  Download, 
  Save, 
  X, 
  Search, 
  AlertCircle,
  Plane,
  Car,
  Bus,
  Ship,
  DollarSign,
  MapPin,
  Clock,
  Eye
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { SearchInput } from '../common/SearchInput';

interface TravelBooking {
  id: string;
  user_id: string;
  trip_id?: string;
  type: 'flight' | 'hotel' | 'car' | 'activity' | 'transport' | 'restaurant' | 'tour' | 'event';
  booking_reference: string;
  provider: string;
  departure?: string;
  arrival?: string;
  start_date: string;
  end_date?: string;
  departure_time?: string;
  arrival_time?: string;
  price?: number;
  currency?: string;
  status: 'confirmed' | 'pending' | 'cancelled' | 'completed';
  confirmation_number?: string;
  location?: string;
  passengers?: string[];
  email_confirmation?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface Trip {
  id: string;
  title: string;
  destination?: string;
}

export function Tickets() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState<TravelBooking[]>([]);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<TravelBooking | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    trip_id: '',
    type: 'flight' as const,
    booking_reference: '',
    provider: '',
    departure_location: '',
    arrival_location: '',
    departure_date: '',
    arrival_date: '',
    departure_time: '',
    arrival_time: '',
    cost: '',
    currency: 'AUD',
    status: 'confirmed' as const,
    confirmation_number: '',
    notes: '',
    file: null as File | null
  });

  const typeOptions = [
    { value: 'flight', label: 'Flight', icon: <Plane size={16} /> },
    { value: 'train', label: 'Train', icon: <Car size={16} /> },
    { value: 'bus', label: 'Bus', icon: <Bus size={16} /> },
    { value: 'car', label: 'Car Rental', icon: <Car size={16} /> },
    { value: 'ship', label: 'Ship/Ferry', icon: <Ship size={16} /> },
    { value: 'other', label: 'Other', icon: <Ticket size={16} /> }
  ];

  const statusOptions = [
    { value: 'confirmed', label: 'Confirmed', color: 'bg-green-100 text-green-800' },
    { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800' }
  ];

  useEffect(() => {
    const loadData = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        const { data: tripsData, error: tripsError } = await supabase
          .from('trips')
          .select('id, title, destination')
          .eq('user_id', user.id)
          .order('start_date', { ascending: true });
          
        if (tripsError) throw tripsError;
        setTrips(tripsData || []);

        const { data: bookingsData, error: bookingsError } = await supabase
          .from('travel_bookings')
          .select('*')
          .eq('user_id', user.id)
          .order('start_date', { ascending: true });
          
        if (bookingsError) throw bookingsError;
        setBookings(bookingsData || []);
      } catch (err) {
        console.error('Error loading data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [user]);

  const handleAddBooking = () => {
    setFormData({
      trip_id: '',
      type: 'flight',
      booking_reference: '',
      provider: '',
      departure_location: '',
      arrival_location: '',
      departure_date: '',
      arrival_date: '',
      departure_time: '',
      arrival_time: '',
      cost: '',
      currency: 'AUD',
      status: 'confirmed',
      confirmation_number: '',
      notes: '',
      file: null
    });
    setIsEditing(false);
    setShowAddModal(true);
  };

  const handleEditBooking = (booking: TravelBooking) => {
    setSelectedBooking(booking);
    setFormData({
      trip_id: booking.trip_id || '',
      type: booking.type as any,
      booking_reference: booking.booking_reference,
      provider: booking.provider,
      departure_location: booking.departure || '',
      arrival_location: booking.arrival || '',
      departure_date: booking.start_date,
      arrival_date: booking.end_date || '',
      departure_time: booking.departure_time || '',
      arrival_time: booking.arrival_time || '',
      cost: booking.price?.toString() || '',
      currency: booking.currency || 'AUD',
      status: booking.status as any,
      confirmation_number: booking.confirmation_number || '',
      notes: booking.notes || '',
      file: null
    });
    setIsEditing(true);
    setShowAddModal(true);
  };

  const handleViewBooking = (booking: TravelBooking) => {
    setSelectedBooking(booking);
    setShowViewModal(true);
  };

  const handleDeleteBooking = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this booking?')) {
      try {
        const { error } = await supabase
          .from('travel_bookings')
          .delete()
          .eq('id', id);
          
        if (error) throw error;
        setBookings(bookings.filter(booking => booking.id !== id));
      } catch (err) {
        console.error('Error deleting booking:', err);
        setError(err instanceof Error ? err.message : 'Failed to delete booking');
      }
    }
  };

  const handleSaveBooking = async () => {
    if (!formData.booking_reference || !formData.provider || !formData.departure_location || !formData.arrival_location || !formData.departure_date) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setUploading(true);

      const bookingData = {
        user_id: user!.id,
        trip_id: formData.trip_id || null,
        type: formData.type,
        booking_reference: formData.booking_reference,
        provider: formData.provider,
        departure: formData.departure_location || null,
        arrival: formData.arrival_location || null,
        start_date: formData.departure_date,
        end_date: formData.arrival_date || null,
        departure_time: formData.departure_time || null,
        arrival_time: formData.arrival_time || null,
        price: formData.cost ? parseFloat(formData.cost) : null,
        currency: formData.currency,
        status: formData.status,
        confirmation_number: formData.confirmation_number || null,
        notes: formData.notes || null
      };

      if (isEditing && selectedBooking) {
        const { data, error } = await supabase
          .from('travel_bookings')
          .update(bookingData)
          .eq('id', selectedBooking.id)
          .select()
          .single();
          
        if (error) throw error;
        setBookings(bookings.map(booking => booking.id === selectedBooking.id ? data : booking));
      } else {
        const { data, error } = await supabase
          .from('travel_bookings')
          .insert(bookingData)
          .select()
          .single();
          
        if (error) throw error;
        setBookings([data, ...bookings]);
      }
      
      setShowAddModal(false);
      setError(null);
    } catch (err) {
      console.error('Error saving booking:', err);
      setError(err instanceof Error ? err.message : 'Failed to save booking');
    } finally {
      setUploading(false);
    }
  };

  const getTypeIcon = (type: string) => {
    return typeOptions.find(t => t.value === type)?.icon || <Ticket size={16} />;
  };

  const getStatusColor = (status: string) => {
    return statusOptions.find(s => s.value === status)?.color || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTripTitle = (tripId: string) => {
    return trips.find(t => t.id === tripId)?.title || 'Unknown Trip';
  };

  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = booking.booking_reference.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         booking.provider.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === 'all' || booking.type === typeFilter;
    const matchesStatus = statusFilter === 'all' || booking.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Travel Bookings</h1>
            <p className="text-gray-600 mt-1">Manage your flight, train, and transportation bookings</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <div className="p-6">
                <div className="h-4 bg-gray-200 rounded mb-4"></div>
                <div className="h-3 bg-gray-200 rounded mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Travel Bookings</h1>
          <p className="text-gray-600 mt-1">Manage your flight, train, and transportation bookings</p>
        </div>
        <Button
          variant="primary"
          onClick={handleAddBooking}
          icon={<Plus size={20} />}
        >
          Add Booking
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="flex gap-4">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search bookings..."
          className="flex-1"
        />
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Types</option>
          {typeOptions.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Status</option>
          {statusOptions.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <AlertCircle className="text-red-500" size={20} />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Bookings Grid */}
      {filteredBookings.length === 0 ? (
        <Card className="text-center py-12">
          <Ticket className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No bookings found</h3>
          <p className="text-gray-600 mb-4">Start by adding your first travel booking</p>
          <Button
            variant="primary"
            onClick={handleAddBooking}
            icon={<Plus size={20} />}
          >
            Add Your First Booking
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBookings.map((booking) => (
            <Card key={booking.id} className="hover:shadow-lg transition-shadow">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{booking.booking_reference}</h3>
                    <div className="flex items-center gap-1 text-sm text-blue-600 mb-2">
                      {getTypeIcon(booking.type)}
                      <span className="capitalize">{booking.type}</span>
                    </div>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                    {booking.status}
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Provider:</span> {booking.provider}
                  </div>
                  {booking.trip_id && (
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Trip:</span> {getTripTitle(booking.trip_id)}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin size={14} />
                    <span>{booking.departure || 'N/A'} → {booking.arrival || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar size={14} />
                    <span>{formatDate(booking.start_date)}</span>
                  </div>
                  {booking.price && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <DollarSign size={14} />
                      <span>{booking.price} {booking.currency}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    Created {formatDate(booking.created_at)}
                  </div>
                  <div className="flex items-center gap-2">
                    {booking.ticket_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(booking.ticket_url, '_blank')}
                        icon={<Download size={14} />}
                      >
                        Download
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewBooking(booking)}
                      icon={<Eye size={14} />}
                    >
                      View
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditBooking(booking)}
                      icon={<Edit size={14} />}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteBooking(booking.id)}
                      icon={<Trash2 size={14} />}
                      className="text-red-600 hover:text-red-700"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={isEditing ? 'Edit Booking' : 'Add New Booking'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Trip (Optional)</label>
            <select
              value={formData.trip_id}
              onChange={(e) => setFormData({ ...formData, trip_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">No specific trip</option>
              {trips.map(trip => (
                <option key={trip.id} value={trip.id}>{trip.title}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {typeOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Booking Reference *</label>
            <input
              type="text"
              value={formData.booking_reference}
              onChange={(e) => setFormData({ ...formData, booking_reference: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., ABC123456"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Provider *</label>
            <input
              type="text"
              value={formData.provider}
              onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Qantas, Uber, etc."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Departure Location *</label>
              <input
                type="text"
                value={formData.departure_location}
                onChange={(e) => setFormData({ ...formData, departure_location: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Sydney Airport"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Arrival Location *</label>
              <input
                type="text"
                value={formData.arrival_location}
                onChange={(e) => setFormData({ ...formData, arrival_location: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Melbourne Airport"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Departure Date *</label>
            <input
              type="date"
              value={formData.departure_date}
              onChange={(e) => setFormData({ ...formData, departure_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cost</label>
            <input
              type="number"
              step="0.01"
              value={formData.cost}
              onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="0.00"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Additional notes..."
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button
            variant="outline"
            onClick={() => setShowAddModal(false)}
            icon={<X size={16} />}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveBooking}
            icon={uploading ? undefined : <Save size={16} />}
            disabled={uploading}
          >
            {uploading ? 'Saving...' : (isEditing ? 'Update Booking' : 'Create Booking')}
          </Button>
        </div>
      </Modal>

      {/* View Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title={selectedBooking?.booking_reference || 'Booking Details'}
      >
        {selectedBooking && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <div className="flex items-center gap-1 text-blue-600">
                  {getTypeIcon(selectedBooking.type)}
                  <span className="capitalize">{selectedBooking.type}</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <div className={`px-2 py-1 rounded-full text-xs font-medium inline-block ${getStatusColor(selectedBooking.status)}`}>
                  {selectedBooking.status}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
              <p className="text-gray-900">{selectedBooking.provider}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Route</label>
              <p className="text-gray-900">{selectedBooking.departure || 'N/A'} → {selectedBooking.arrival || 'N/A'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Departure Date</label>
              <p className="text-gray-900">{formatDate(selectedBooking.start_date)}</p>
            </div>

            {selectedBooking.price && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                <p className="text-gray-900">{selectedBooking.price} {selectedBooking.currency}</p>
              </div>
            )}

            {selectedBooking.notes && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <p className="text-gray-900">{selectedBooking.notes}</p>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6">
          {selectedBooking?.ticket_url && (
            <Button
              variant="outline"
              onClick={() => window.open(selectedBooking.ticket_url, '_blank')}
              icon={<Download size={16} />}
            >
              Download Ticket
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setShowViewModal(false)}
          >
            Close
          </Button>
          {selectedBooking && (
            <Button
              variant="primary"
              onClick={() => {
                setShowViewModal(false);
                handleEditBooking(selectedBooking);
              }}
              icon={<Edit size={16} />}
            >
              Edit Booking
            </Button>
          )}
        </div>
      </Modal>
    </div>
  );
}